import * as React from 'react';
import { HeaderFilterTable, Icon } from '@/components/common';
import { Options } from '@/components/ui/combobox';
import Icons from '@/const/icons';
import { useInfiniteQuery } from '@tanstack/react-query';
import { GET_CLIENT_DIRECTORY_SEGMENTATION_FILTER } from '@/api';
import { clientDirectoryService } from '@/api/clientDirectory';
import { getSegmentClientColor } from '@/lib';
import { SegmentationFilterListData } from '@/interfaces';
import { useFilters } from '@/hooks';
import { useDebouncedCallback } from 'use-debounce';
import { useRouter } from 'next/router';
import AppRoutes from '@/const/routes';
import { GC_TIME, STALE_TIME } from '@/const/common';

interface SegmentFilterProps {
	title?: string;
	buttonContentDefault?: boolean;
}

export const SegmentFilter: React.FC<SegmentFilterProps> = ({
	buttonContentDefault = false,
}) => {
	const LIMIT_PER_PAGE = 50;
	const { hasFilter, params, replaceUrl } = useFilters();
	const router = useRouter();
	const observer = React.useRef<IntersectionObserver | null>(null);
	const [data, setData] = React.useState<Options[]>([]);
	const [selected, setSelected] = React.useState<string[]>([]);
	const debounced = useDebouncedCallback((value: string[]) => {
		params.delete('segmentId');
		if (value.length > 0) {
			value.forEach((id) => {
				params.append('segmentId', id);
			});
		}
		replaceUrl('1');
	}, 1000);

	React.useEffect(() => {
		const segments = router.query.segmentId;
		if (segments) {
			if (Array.isArray(segments)) {
				setSelected(segments);
			} else {
				setSelected([segments]);
			}
		} else {
			setSelected([]);
		}
	}, [router.query.segmentId]);

	const fetchData = async ({
		pageParam,
	}: {
		pageParam: number;
	}): Promise<SegmentationFilterListData> => {
		const response = await clientDirectoryService.getSegmentationListFilter(
			pageParam,
			LIMIT_PER_PAGE
		);
		return response.data;
	};

	const {
		data: segmentationList,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		// isError,
		// isLoading,
	} = useInfiniteQuery({
		initialData: undefined,
		initialPageParam: 1,
		queryKey: [GET_CLIENT_DIRECTORY_SEGMENTATION_FILTER],
		queryFn: fetchData,
		staleTime: STALE_TIME,
		gcTime: GC_TIME,
		refetchOnWindowFocus: false,
		getNextPageParam(lastPage) {
			return lastPage.page < lastPage.totalPages
				? lastPage.page + 1
				: undefined;
		},
	});

	const lastItemCallback = React.useCallback(
		(node: HTMLDivElement | null) => {
			if (isFetchingNextPage) return;
			if (observer.current) observer.current.disconnect();
			observer.current = new IntersectionObserver((entries) => {
				if (entries[0].isIntersecting && hasNextPage) {
					fetchNextPage();
				}
			});
			if (node) observer.current.observe(node);
		},
		[isFetchingNextPage, hasNextPage]
	);

	React.useEffect(() => {
		if (segmentationList) {
			const auxList: Options[] = [];
			let listFlatMapped =
				segmentationList?.pages.flatMap((page) => page.items) || [];

			if (router.pathname !== AppRoutes.LAWYERAPP_CLIENT_DIRECTORY) {
				listFlatMapped = listFlatMapped.filter((item) => Number(item.id) > 0);
			}
			listFlatMapped.map((item) =>
				auxList.push({
					label: item.name,
					value: item.id?.toString(),
					icon: (
						<Icon
							src={Icons.profile}
							width={18}
							height={18}
							className={`flex-shrink-0 !text-[${getSegmentClientColor(
								item.name
							)}]`}
						/>
					),
				})
			);
			setData(auxList);
		}
	}, [segmentationList]);

	const handleSelectFilters = (value?: string) => {
		if (value) {
			const newSelected = selected.includes(value)
				? selected.filter((s) => s !== value)
				: [...selected, value];
			setSelected(newSelected);
			debounced(newSelected);
		}
	};

	const handleClearAll = () => {
		setSelected([]);
		debounced([]);
	};

	return (
		<HeaderFilterTable
			title="Segment"
			searchTitle="Segment"
			type="multiple"
			values={selected}
			filterOptions={data}
			containerRef={lastItemCallback}
			handleClearAll={handleClearAll}
			handleSelectFilters={handleSelectFilters}
			hasFilter={hasFilter('segmentId')}
			classNamePopoverContent="lg:w-[302px]"
			buttonContentDefault={buttonContentDefault}
		/>
	);
};
